import { callbackRoute, PrivateRoute } from '@airship-platform/airship-auth';
import {
  AppointmentProposalShareProvider,
  AppointmentProposalSurveyOptionsProvider,
  BaseTemplate,
  composeProviders,
  OrganizationProvider,
  OrganizationUsersProvider,
  SystemTypesProvider,
  withPageProps,
} from '@airship-platform/airship-patterns';
import {
  ManageProductsProvider,
  OrganizationBusinessUnitsProvider,
  OrganizationSOWsProvider,
  ProposalOptionNameDefaultsProvider,
} from '@airship-platform/airship-proposal';
import {
  NotFoundPage as BaseNotFoundPage,
  currentCustomerExperienceOptions,
  customerExperiencePrioritiesOptions,
  Loader,
} from '@airship-platform/jam-web/components';
import { lazy, PropsWithChildren, Suspense } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router';
import { AppointmentEquipmentProvider } from './data-access/appointmentEquipment';
import { AppointmentHistoryProvider } from './data-access/appointmentHistory';
import { AppointmentProposalUpgradesProvider } from './data-access/appointmentProposalUpgrades';
import { FinancingConfigProvider } from './data-access/financingConfig';
import { JobsActivitiesProvider } from './data-access/jobsActivitiesTracking';
import { ManualJAgentProvider } from './data-access/manualJAgent';
import { PriceDisplayModeProvider } from './data-access/price-display-mode';
import { PricebookUniversalUpgradeProductsProvider } from './data-access/pricebook-universal-upgrades-product';
import { ProposalEngagementProvider } from './data-access/proposalEngagement';
import { ProposalHeaderImageProvider } from './data-access/proposalHeaderImage';
import { ProposalIntegrationsProvider } from './data-access/proposalIntegrations';
import { ProposalQualityScoreProvider } from './data-access/proposalQualityScore';
import { ProposalTemplatesProvider } from './data-access/proposalTemplate';
import { ServiceHistoryProvider } from './data-access/serviceHistory';
import {
  AppointmentProposalCostsProvider,
  AppointmentProposalSyncProvider,
  OfflineAppointmentProposalOptionModifiersProvider,
  OfflineAppointmentProposalOptionProductsProvider,
  OfflineAppointmentProposalOptionSowsProvider,
  OfflineAppointmentProposalOptionsProvider,
  OfflineAppointmentProposalsProvider,
  OfflineAppointmentsProvider,
  OfflineMembershipDataProvider,
  OfflineOrgContentProvider,
  OfflinePricebookProvider,
} from './data-offline';
import AppointmentPage from './features/appointment/AppointmentPage';
import { DataCollectionPage } from './features/data-collection';
import { AddModifierPage, EditModifierPage } from './features/modifier-query';
import { OfflineDiagnosticsPage } from './features/offline-diagnostics';
import {
  DiscountsQueryView,
  ProductsQueryView,
} from './features/products-query';
import {
  CaptureMediaPage,
  CaptureNotesPage,
  CustomerExperiencePage,
  DocumentsPage,
  OutageExperiencePage,
  SendSurveyPage,
} from './features/proposal-builder';
import ActivityPage from './features/proposal-builder/components/ActivityPage';
import DownPaymentPage from './features/proposal-builder/components/DownPaymentPage';
import EditPresentationPage from './features/proposal-builder/components/EditPresentationPage';
import FinancingPage from './features/proposal-builder/components/FinancingPage';
import FollowUpPage from './features/proposal-builder/components/FollowUpPage';
import JobMediaPage from './features/proposal-builder/components/JobMediaPage';
import LeasingPlanPage from './features/proposal-builder/components/LeasingPlanPage';
import SyncStatusPage from './features/proposal-builder/components/SyncStatusPage';
import CreateUpgradePage from './features/proposal-builder/components/upgrades-section/CreateUpgradePage';
import UpgradesPage from './features/proposal-builder/components/UpgradesPage';
import ProposalNavigationPage from './features/proposal-navigation/ProposalNavigationPage';
import EditProposalOptionSummaryPage from './features/proposal-option-summary/EditProposalOptionSummaryPage';
import { ProposalOptionsView } from './features/proposal-options';
import { ManageOptionPage } from './features/proposal-options-manage';
import {
  CustomerProposalPage,
  ProposalPresentationPage,
} from './features/proposal-presentation';
import {
  AddProductPage,
  EditProductPage,
  ModifierQueryPage,
  OptionBuilderPage,
  ProductsQueryPage,
  ProposalPage,
  SOWQueryPage,
} from './features/proposals';
import {
  AppointmentHistoryView,
  ContactsPage,
  ScheduleDetailsPage,
  SchedulePage,
  ScheduleView,
} from './features/schedule';
import AppointmentSearchFilter from './features/schedule/components/forms/AppointmentSearchFilter';
import { AddSOWPage, EditSOWPage } from './features/sow-query';
import { BaseTemplateAuth } from './patterns';
import ROUTE_PATHS from './routes/paths';

const PresentProposalPage = lazy(
  () => import('./features/proposal-presentation/PresentProposalPage')
);

const BoundSurveyOptionsProvider: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <AppointmentProposalSurveyOptionsProvider
    currentCustomerExperienceOptions={currentCustomerExperienceOptions}
    customerExperiencePrioritiesOptions={customerExperiencePrioritiesOptions}
  >
    {children}
  </AppointmentProposalSurveyOptionsProvider>
);

const OptionBuilderComposedProviders = composeProviders(ManageProductsProvider);

const ProposalComposedProviders = composeProviders(
  AppointmentProposalShareProvider,
  ProposalQualityScoreProvider
);

const ServiceManagementComposedProviders = composeProviders(
  OrganizationProvider,
  OrganizationBusinessUnitsProvider,
  OrganizationUsersProvider,
  AppointmentHistoryProvider,
  ServiceHistoryProvider,
  AppointmentEquipmentProvider,
  ProposalOptionNameDefaultsProvider,
  SystemTypesProvider,
  BoundSurveyOptionsProvider,
  PriceDisplayModeProvider,
  ProposalHeaderImageProvider,
  OrganizationSOWsProvider,
  FinancingConfigProvider,
  ProposalTemplatesProvider,
  ManualJAgentProvider,
  PricebookUniversalUpgradeProductsProvider,
  JobsActivitiesProvider,
  AppointmentProposalUpgradesProvider,
  ProposalIntegrationsProvider,
  ProposalEngagementProvider
);

const OfflineComposedProviders = composeProviders(
  AppointmentProposalSyncProvider,
  OfflineOrgContentProvider,
  OfflinePricebookProvider,
  OfflineMembershipDataProvider,
  OfflineAppointmentsProvider,
  OfflineAppointmentProposalsProvider,
  OfflineAppointmentProposalOptionsProvider,
  OfflineAppointmentProposalOptionProductsProvider,
  OfflineAppointmentProposalOptionModifiersProvider,
  OfflineAppointmentProposalOptionSowsProvider,
  AppointmentProposalCostsProvider
);

const DataCollectionPageOutlet = withPageProps(DataCollectionPage, {
  title: 'Customer Appointment Survey',
});
const CustomerProposalPageOutlet = withPageProps(CustomerProposalPage, {
  title: 'Customer Proposal Presentation View Redirect',
});

const proposalRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to={'options'} replace={true} />,
  },
  {
    path: 'service-menu',
    Component: withPageProps(ProposalNavigationPage, {
      title: 'Service Call Menu',
    }),
  },
  {
    path: 'edit-presentation',
    Component: withPageProps(EditPresentationPage, {
      title: 'Edit Proposal Presentation',
    }),
  },
  {
    path: 'sync-status',
    Component: withPageProps(SyncStatusPage, {
      title: 'Options Sync Status',
    }),
  },
  {
    path: 'financing',
    Component: withPageProps(FinancingPage, {
      title: 'Financing',
    }),
  },
  {
    path: 'financing/down-payment',
    Component: withPageProps(DownPaymentPage, { title: 'Down Payment' }),
  },
  {
    path: 'financing/leasing-plan',
    Component: withPageProps(LeasingPlanPage, { title: 'Leasing Plan' }),
  },
  {
    path: 'upgrades',
    Component: withPageProps(UpgradesPage, {
      title: 'Upgrades',
    }),
  },
  {
    path: 'upgrades/create',
    Component: withPageProps(CreateUpgradePage, {
      title: 'Create Upgrade',
    }),
  },
  {
    path: 'documents',
    Component: withPageProps(DocumentsPage, {
      title: 'Documents',
    }),
  },
  {
    path: 'contacts',
    Component: withPageProps(ContactsPage, {
      title: 'Customer Contacts',
    }),
  },
  {
    path: 'customer-experience',
    Component: withPageProps(CustomerExperiencePage, {
      title: 'Customer Experience',
    }),
  },
  {
    path: 'outage-experience',
    Component: withPageProps(OutageExperiencePage, {
      title: 'Outage Experience',
    }),
  },
  {
    path: 'capture-media',
    Component: withPageProps(CaptureMediaPage, {
      title: 'Capture Media',
    }),
  },
  {
    path: 'capture-media/job-media',
    Component: withPageProps(JobMediaPage, {
      title: 'Job Media',
    }),
  },
  {
    path: 'notes',
    Component: withPageProps(CaptureNotesPage, {
      title: 'Capture Notes',
    }),
  },
  {
    path: 'send-survey',
    Component: withPageProps(SendSurveyPage, {
      title: 'Send Data Collection Survey',
    }),
  },
  {
    path: 'summary',
    Component: withPageProps(ProposalPresentationPage, {
      title: 'Technician Proposal Presentation View Redirect',
    }),
    children: [{ path: '*' }],
  },
  {
    path: 'follow-up',
    Component: withPageProps(FollowUpPage, {
      title: 'Proposal Follow Up',
    }),
  },
  {
    path: 'activity',
    Component: withPageProps(ActivityPage, {
      title: 'Proposal Activity',
    }),
  },
  {
    path: 'options/manage/*',
    Component: ManageOptionPage,
  },
  {
    path: 'options',
    element: (
      <OptionBuilderComposedProviders>
        <OptionBuilderPage />
      </OptionBuilderComposedProviders>
    ),
    children: [
      {
        path: ':appointmentProposalOptionId',
        children: [
          {
            index: true,
            Component: withPageProps(ProposalOptionsView, {
              title: 'Proposal Options Builder',
              scrollToTop: true,
            }),
          },
          {
            path: 'product/:appointmentProposalOptionProductId/',
            Component: withPageProps(EditProductPage, {
              title: 'Proposal Options Product Edit',
              scrollToTop: true,
            }),
          },
          {
            path: 'modifier/:appointmentProposalOptionModifierId/',
            Component: withPageProps(EditModifierPage, {
              title: 'Proposal Options Modifier Edit',
              scrollToTop: true,
            }),
          },
          {
            path: 'sow/:appointmentProposalOptionSowId/',
            Component: withPageProps(EditSOWPage, {
              title: 'Proposal Options SOW Edit',
              scrollToTop: true,
            }),
          },
          {
            path: 'queryProducts',
            element: <Navigate to={'../products'} replace={true} />,
          },
          {
            path: 'products',
            element: <ProductsQueryPage />,
            children: [
              {
                index: true,
                Component: withPageProps(ProductsQueryView, {
                  title: 'Proposal Options Products Query',
                }),
              },
              {
                path: 'discounts',
                Component: withPageProps(DiscountsQueryView, {
                  title: 'Proposal Options Discounts Query',
                }),
              },
              {
                path: ':productId',
                Component: withPageProps(AddProductPage, {
                  title: 'Proposal Options Add Product',
                  scrollToTop: true,
                }),
              },
            ],
          },
          {
            path: 'summary',
            Component: withPageProps(EditProposalOptionSummaryPage, {
              title: 'Proposal Options Summary',
            }),
          },
          //matheusgrandi: this route can be removed after the next release
          {
            path: 'queryModifiers',
            element: <Navigate to={'../query-modifiers'} replace={true} />,
          },
          {
            path: 'query-modifiers',
            children: [
              {
                index: true,
                Component: withPageProps(ModifierQueryPage, {
                  title: 'Proposal Options Modifiers Query',
                }),
              },
              {
                path: ':discountsAndFeesId',
                Component: withPageProps(AddModifierPage, {
                  title: 'Proposal Add Modifier',
                  scrollToTop: true,
                }),
              },
            ],
          },
          {
            path: 'query-sow',
            children: [
              {
                index: true,
                Component: withPageProps(SOWQueryPage, {
                  title: 'Proposal Options SOW Query',
                }),
              },
              {
                path: ':sowId',
                Component: withPageProps(AddSOWPage, {
                  title: 'Proposal Add SOW',
                  scrollToTop: true,
                }),
              },
            ],
          },
        ],
      },
    ],
  },
];

const scheduleRoutes: RouteObject[] = [
  {
    Component: SchedulePage,
    children: [
      {
        index: true,
        Component: withPageProps(ScheduleView, {
          title: 'Technician Appointments',
          scrollToTop: true,
        }),
      },
      {
        path: 'search',
        Component: withPageProps(AppointmentHistoryView, {
          title: 'Technician Appointments Search',
        }),
      },
    ],
  },
  {
    path: ':appointmentId',
    Component: AppointmentPage,
    children: [
      {
        index: true,
        Component: withPageProps(ScheduleDetailsPage, {
          title: 'Customer Appointment',
        }),
      },
      {
        path: 'contacts',
        Component: withPageProps(ContactsPage, {
          title: 'Customer Contacts',
        }),
      },
      {
        path: 'proposal/:appointmentProposalId/',
        element: (
          <ProposalComposedProviders>
            <ProposalPage />
          </ProposalComposedProviders>
        ),
        children: proposalRoutes,
      },
    ],
  },
  {
    path: 'search/filters',
    Component: withPageProps(AppointmentSearchFilter, {
      title: 'Technician Appointments Search Filters',
    }),
  },
  {
    path: 'offline-diagnostics',
    Component: withPageProps(OfflineDiagnosticsPage, {
      title: 'Offline Sync Diagnostics',
    }),
  },
];

const routes: RouteObject[] = [
  callbackRoute(),
  {
    path: '*',
    Component: withPageProps(BaseNotFoundPage, { title: 'Page not found' }),
  },
  {
    path: ROUTE_PATHS.HOME,
    element: <Navigate to={ROUTE_PATHS.SCHEDULE} replace={true} />,
  },
  {
    path: ROUTE_PATHS.PROPOSAL_CUSTOMER_VIEW,
    Component: CustomerProposalPageOutlet,
    children: [{ path: '*' }],
  },
  {
    path: ROUTE_PATHS.DATA_COLLECTION,
    element: <BaseTemplate navBarProps={{ hideNavbar: true }} />,
    children: [
      {
        index: true,
        Component: DataCollectionPageOutlet,
      },
    ],
  },
  {
    path: ROUTE_PATHS.SCHEDULE,
    element: (
      <PrivateRoute loadingComponent={<Loader isLoading />}>
        <ServiceManagementComposedProviders>
          <OfflineComposedProviders>
            <Outlet />
          </OfflineComposedProviders>
        </ServiceManagementComposedProviders>
      </PrivateRoute>
    ),
    children: [
      {
        element: <BaseTemplateAuth />,
        children: scheduleRoutes,
      },
      {
        path: ':appointmentId/proposal/:appointmentProposalId/present/*',
        element: (
          <Suspense fallback={<Loader isLoading />}>
            <PresentProposalPage />
          </Suspense>
        ),
      },
    ],
  },
];

export default routes;
